import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink, Github } from "lucide-react";
import Nav from "@/components/Nav.jsx";
import Footer from "@/components/sections/Footer.jsx";
import { projects, getProject } from "@/data/projects";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = getProject(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!project) {
    return (
      <main data-testid="project-not-found" className="min-h-screen bg-[#050505] text-[#F5F5F5]">
        <Nav />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-32">
          <div className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66] mb-4">
            [ERR_404]
          </div>
          <h1 className="font-['Unbounded'] font-black text-5xl lg:text-7xl leading-none">
            SIGNAL_LOST
          </h1>
          <p className="mt-6 text-[#A1A1AA] max-w-lg">
            No case study registered for slug: <span className="text-[#00FF66]">{slug}</span>
          </p>
          <button
            data-testid="project-notfound-back"
            onClick={() => navigate("/")}
            className="mt-10 inline-flex items-center gap-3 border border-[#00FF66] text-[#00FF66] px-5 py-3 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] hover:bg-[#00FF66] hover:text-black"
            style={{ transition: "background-color 0.15s, color 0.15s" }}
          >
            <ArrowLeft size={12} /> RETURN_HOME
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  const currentIdx = projects.findIndex((p) => p.slug === slug);
  const next = projects[(currentIdx + 1) % projects.length];

  return (
    <main data-testid={`project-detail-${project.slug}`} className="min-h-screen bg-[#050505] text-[#F5F5F5]">
      <Nav />

      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${project.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.9) contrast(1.1)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/70 to-[#050505]" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, #00FF66 0 1px, transparent 1px 40px)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-32 lg:pt-40 pb-16 lg:pb-20">
          <Link
            to="/"
            data-testid="project-back-link"
            className="inline-flex items-center gap-2 font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA] hover:text-[#00FF66] transition-colors"
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> RETURN_TO_INDEX
          </Link>

          <div className="mt-8 flex items-center gap-3 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66]">
            <span>[{project.id}]</span>
            <span className="text-[#A1A1AA]">// {project.client}</span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 font-['Unbounded'] font-black text-4xl sm:text-5xl md:text-6xl lg:text-[6.5rem] leading-[0.9] tracking-tighter"
          >
            {project.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-[#A1A1AA] max-w-3xl text-base md:text-lg leading-relaxed"
          >
            {project.summary}
          </motion.p>

          {/* Meta strip */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8 font-['JetBrains_Mono']">
            <MetaCell label="YEAR" value={project.year} />
            <MetaCell label="DURATION" value={project.duration} />
            <MetaCell label="ROLE" value={project.role} />
            <MetaCell label="SCOPE" value={project.scope.join(" · ")} />
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="border-b border-white/10 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 grid grid-cols-3 gap-6">
          {project.metrics.map((m) => (
            <div key={m.v} data-testid={`detail-metric-${m.v.toLowerCase()}`}>
              <div className="font-['Unbounded'] font-black text-3xl md:text-5xl text-[#F5F5F5]">
                {m.k}
              </div>
              <div className="mt-2 font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA]">
                {m.v}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-16">
          <Block label="01" title="Briefing">
            <p className="text-[#D4D4D8] leading-relaxed text-lg">{project.briefing}</p>
          </Block>

          <Block label="02" title="Approach">
            <ol className="space-y-5">
              {project.approach.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66] pt-1 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[#D4D4D8] leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </Block>

          <Block label="03" title="Outcome">
            <ul className="space-y-4">
              {project.outcome.map((o, i) => (
                <li key={i} className="flex gap-4">
                  <span className="w-1.5 h-1.5 mt-3 shrink-0 bg-[#00FF66]" />
                  <span className="text-[#D4D4D8] leading-relaxed">{o}</span>
                </li>
              ))}
            </ul>
          </Block>

          <Block label="04" title="Testimonial">
            <blockquote className="border-l-2 border-[#00FF66] pl-6 py-2">
              <p className="font-['Unbounded'] text-xl md:text-2xl leading-snug text-[#F5F5F5]">
                &ldquo;{project.quote.text}&rdquo;
              </p>
              <footer className="mt-4 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#A1A1AA]">
                — {project.quote.by}
              </footer>
            </blockquote>
          </Block>
        </div>

        <aside className="lg:col-span-4 lg:sticky lg:top-24 h-max space-y-8">
          <div className="border border-white/10 bg-[#0A0A0A] p-6">
            <div className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#00FF66] mb-4">
              [TOOLING]
            </div>
            <ul className="flex flex-wrap gap-2">
              {project.tooling.map((t) => (
                <li
                  key={t}
                  className="font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] text-[#F5F5F5] border border-white/15 px-2 py-1"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-white/10 bg-[#0A0A0A] p-6">
            <div className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#00FF66] mb-3">
              [METHODOLOGY_NOTE]
            </div>
            <p className="font-['JetBrains_Mono'] text-[12px] leading-relaxed text-[#A1A1AA]">
              Full operational detail available under NDA. Client-specific TTPs and
              artifacts are withheld to protect ongoing operations.
            </p>
          </div>

          <Link
            to="/#contact"
            data-testid="detail-cta-contact"
            className="group inline-flex items-center justify-between gap-3 border border-[#00FF66] text-[#00FF66] px-5 py-4 w-full font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] hover:bg-[#00FF66] hover:text-black"
            style={{ transition: "background-color 0.15s, color 0.15s" }}
          >
            LET'S TALK ABOUT THIS WORK
            <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5" style={{ transition: "transform 0.15s" }} />
          </Link>

          {project.githubUrl && (
            <a
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-github-link"
              className="group inline-flex items-center justify-between gap-3 border border-white/15 text-[#F5F5F5] px-5 py-4 w-full font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] hover:border-[#00FF66] hover:text-[#00FF66]"
              style={{ transition: "border-color 0.15s, color 0.15s" }}
            >
              <span className="inline-flex items-center gap-3">
                <Github size={14} strokeWidth={1.5} />
                VIEW ON GITHUB
              </span>
              <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5" style={{ transition: "transform 0.15s" }} />
            </a>
          )}
        </aside>
      </section>

      {/* Next case — hide when there's only one project */}
      {projects.length > 1 && (
        <section className="border-t border-white/10 bg-[#080808]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
            <Link
              to={`/projects/${next.slug}`}
              data-testid="project-next-link"
              className="group block"
            >
              <div className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-4">
                NEXT_CASE // [{next.id}]
              </div>
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-['Unbounded'] font-bold text-3xl md:text-5xl lg:text-6xl tracking-tight text-[#F5F5F5] group-hover:text-[#00FF66] transition-colors">
                  {next.title}
                </h3>
                <ExternalLink
                  size={28}
                  strokeWidth={1.5}
                  className="text-[#A1A1AA] group-hover:text-[#00FF66] group-hover:-translate-y-1 group-hover:translate-x-1"
                  style={{ transition: "transform 0.2s, color 0.2s" }}
                />
              </div>
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}

function MetaCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] text-[#00FF66]">{label}</div>
      <div className="mt-2 text-[#F5F5F5] text-sm">{value}</div>
    </div>
  );
}

function Block({ label, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <span className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66]">
          [{label}]
        </span>
        <span className="h-px flex-1 bg-white/10" />
        <h2 className="font-['Unbounded'] font-bold text-xl md:text-2xl uppercase tracking-tight">
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}
